import {
	FormList,
	FormShape,
	type GetFormInput,
	type GetFormInputSetter,
	type GetFormOutputVerbose,
} from "@suse-o11y/signal/form";

import { readonly } from "~/tools/readonly";

import type { WidgetFormSync } from "../_sync";
import { FormState as CalculationFormState } from "../calculation/form-state";
import { FormState as ColorFormState } from "../color/form-state";
import { FormState as DecimalsFormState } from "../decimals/form-state";
import { FormState as FontSizeFormState } from "../font-size/form-state";
import { FormState as MetricLabelFormState } from "../metric-label/form-state";
import { FormState as SparklineFormState } from "../sparkline/form-state";
import { StepFormState as ThresholdsStepFormState } from "../thresholds/form-state";
import { FormState as UnitFormState } from "../unit/form-state";

import { DEFAULT as DEFAULT_SETTINGS, type Settings } from "./settings";

type FormState = FormShape<{
	unit: UnitFormState;
	decimals: DecimalsFormState;
	sparkline: SparklineFormState;
	calculation: CalculationFormState;
	metricLabel: MetricLabelFormState;
	fontSize: FontSizeFormState;
	color: ColorFormState;
	thresholds: FormList<ThresholdsStepFormState>;
}>;

const INITIAL: GetFormInput<FormState> = {
	unit: UnitFormState.initial,
	decimals: DecimalsFormState.initial,
	sparkline: DEFAULT_SETTINGS.sparkline,
	calculation: DEFAULT_SETTINGS.calculation,
	metricLabel: MetricLabelFormState.initial,
	fontSize: FontSizeFormState.initial,
	color: DEFAULT_SETTINGS.color,
	thresholds: [],
};

function fromSettings(settings: undefined | Settings) {
	return {
		unit: settings?.unit,
		decimals: settings?.decimals?.toString(),
		sparkline: settings?.sparkline,
		calculation: settings?.calculation?.toString(),
		metricLabel: settings?.metricLabel,
		fontSize: settings?.fontSize?.toString(),
		color: settings?.color,
		thresholds: readonly(settings?.thresholds?.map(ThresholdsStepFormState.fromSettings)),
	} satisfies GetFormInputSetter<FormState>;
}

type Input = ReturnType<typeof fromSettings>;

function init(initial: Input): FormState {
	return FormShape({
		unit: UnitFormState.init(initial.unit),
		decimals: DecimalsFormState.init(initial.decimals),
		sparkline: SparklineFormState.init(initial.sparkline ?? INITIAL.sparkline),
		calculation: CalculationFormState.init(initial.calculation ?? INITIAL.calculation),
		metricLabel: MetricLabelFormState.init(initial.metricLabel),
		fontSize: FontSizeFormState.init(initial.fontSize),
		color: ColorFormState.init(initial.color ?? INITIAL.color),
		thresholds: FormList(ThresholdsStepFormState.init, {
			initial: initial.thresholds ?? INITIAL.thresholds,
		}),
	});
}

function toSettings(output: GetFormOutputVerbose<FormState>): Settings {
	return {
		unit: output.unit ?? undefined,
		decimals: output.decimals ?? undefined,
		sparkline: output.sparkline ?? undefined,
		calculation: output.calculation ?? undefined,
		metricLabel: output.metricLabel ?? undefined,
		fontSize: output.fontSize ?? undefined,
		color: output.color ?? undefined,
		thresholds: output.thresholds.map((step) => ({
			color: step.color ?? undefined,
			value: step.value ?? undefined,
		})),
	};
}

function fromSync({
	unit,
	decimals,
	sparkline,
	calculation,
	metricLabel,
	fontSize,
	color,
	thresholds,
}: WidgetFormSync): Input {
	return {
		unit,
		decimals,
		sparkline,
		calculation,
		metricLabel,
		fontSize,
		color,
		thresholds: thresholds?.steps,
	};
}

function toSync({
	unit,
	decimals,
	sparkline,
	calculation,
	metricLabel,
	fontSize,
	color,
	thresholds,
}: Input): WidgetFormSync {
	return {
		unit,
		decimals,
		sparkline,
		calculation,
		metricLabel,
		fontSize,
		color,
		thresholds: {
			steps: thresholds,
		},
	};
}

const FormState = {
	initial: INITIAL,
	init,
	fromSettings,
	toSettings,
	fromSync,
	toSync,
} as const;

export { FormState };
